import {
  resolveV1Image,
  type RawConfigurationsV1,
  type RawFeature,
} from "../formats/v1";
import type {
  RawConfigurationsV2,
  RawFeaturePropertiesV2,
  RawFeatureV2,
} from "../formats/v2";
import type { RawDocument } from "../formats/shared";

/** Un fichier par palier de migration (voir `jsonLoader.ts`'s `MIGRATIONS`, principe général
 * en BESOINS.md §8.1) — celui-ci migre un document v1 vers v2 (détail du format v2 en §8.2). */

/**
 * Migre un unique feature v1 (`../formats/v1.ts`'s `RawFeature`) vers son équivalent v2 — opère sur
 * les données BRUTES (pas sur `Shape`), pour préserver tel quel tout champ `extra` inconnu du
 * modèle interne (résiste ainsi à l'ajout futur de tout champ `extra` pas encore mappé sur
 * `Shape`, sans avoir à toucher cette migration). Réutilise `resolveV1Image` (même logique de
 * priorité que le chargement réel v1, voir son commentaire).
 *
 * `resolvedImage.isDirect` produit `extra.type: "image"` (voir `Shape.shapeKind`/`v2.ts`), au lieu
 * de forwarder le `properties.type` v1 d'origine (typiquement `"rectangle"` ou absent, puisqu'une
 * image directe v1 était toujours dessinée comme un rectangle 4 sommets) — jamais en
 * `extra.image.isDirect` (retiré de `ShapeImage`, voir `v2.ts`). `properties.type` (v1, sibling de
 * `extra`) N'est repris tel quel que si l'image n'est PAS directe, simplement déplacé sous
 * `properties.extra.type` (v2, voir `v2.ts`), sans réécriture de sa valeur.
 */
function migrateV1FeatureToV2(feature: RawFeature): RawFeatureV2 {
  const properties = feature.geometry?.properties;
  if (!properties) return feature as unknown as RawFeatureV2;

  const { type, ...restProperties } = properties;
  const extra = properties.extra ?? {};
  const resolvedImage = resolveV1Image(properties);
  const image = resolvedImage
    ? {
        src: resolvedImage.src,
        width: resolvedImage.width,
        height: resolvedImage.height,
        scale: resolvedImage.scale,
        rotation: resolvedImage.rotation,
        ...(extra.imageSvg !== undefined ? { svg: extra.imageSvg } : {}),
      }
    : undefined;

  const { icon: _icon, imageSrc: _imageSrc, imageSvg: _imageSvg, ...restExtra } = extra;
  const newExtra: RawFeaturePropertiesV2["extra"] = {
    ...(resolvedImage?.isDirect ? { type: "image" } : type !== undefined ? { type } : {}),
    ...restExtra,
    ...(image ? { image } : {}),
  };

  // Retire uniquement entity.entity.image (repris ci-dessus dans extra.image le cas échéant) —
  // le reste d'entity.entity (label, reference, fields...) et d'entity lui-même reste inchangé.
  const entity = properties.entity;
  const inner = typeof entity === "object" && entity !== null ? (entity as { entity?: unknown }).entity : undefined;
  const newEntity =
    typeof inner === "object" && inner !== null && "image" in inner
      ? { ...(entity as Record<string, unknown>), entity: { ...(inner as Record<string, unknown>), image: undefined } }
      : entity;

  return {
    ...feature,
    geometry: {
      ...feature.geometry,
      properties: {
        ...restProperties,
        extra: newExtra,
        entity: newEntity,
      },
    },
  };
}

/**
 * Migre `configurations.featureStyle` (v1) vers `configurations.defaultShapeStyle` (v2, voir
 * `../formats/v2.ts`) — même donnée, juste renommée — et pose `configurations.formatVersion: 2`
 * (voir `../formats/shared.ts`'s `RawConfigurations.formatVersion`) : `configurations` est donc
 * TOUJOURS présent en sortie, même absent en entrée, puisque c'est désormais le seul endroit qui
 * porte la version du format.
 */
function migrateV1ConfigurationsToV2(
  configurations: RawConfigurationsV1 | undefined,
): RawConfigurationsV2 {
  const { featureStyle, ...rest } = configurations ?? {};
  return {
    ...rest,
    formatVersion: 2,
    ...(featureStyle !== undefined ? { defaultShapeStyle: featureStyle } : {}),
  };
}

/**
 * Migre un document v1 entier (`../formats/v1.ts`'s `RawDocument<RawFeature, RawConfigurationsV1>`)
 * vers v2 — voir `migrateV1FeatureToV2` pour la conversion par feature et
 * `migrateV1ConfigurationsToV2` pour `configurations.featureStyle` -> `defaultShapeStyle` (et la
 * pose de `configurations.formatVersion: 2`). `layers`/`extras` restent eux structurellement
 * identiques entre v1 et v2 (voir BESOINS.md §8.2) : seule `configurations` change (renommage +
 * `formatVersion`), et chaque feature est transformé individuellement.
 */
export function migrateV1DocumentToV2(raw: unknown): unknown {
  const doc = raw as RawDocument<RawFeature, RawConfigurationsV1> | undefined;
  return {
    ...doc,
    configurations: migrateV1ConfigurationsToV2(doc?.configurations),
    layers: Object.fromEntries(
      Object.entries(doc?.layers ?? {}).map(([layerKey, layer]) => [
        layerKey,
        { ...layer, features: (layer.features ?? []).map(migrateV1FeatureToV2) },
      ]),
    ),
  };
}
